import React, { useState, useRef, useEffect } from "react";
import { Send, X, MessageCircle, Bot, History, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { ChatSource } from "../chat.service";
import { useChatStore } from "../chat.store";
import { ChatHistory } from "./chatHistory.component";
import { WorkspaceSelector } from "./workspaceSelector.component";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useMobileChatGestures } from "./mobileChatGestures.hook";
import { useTranslation } from "react-i18next";
import { useSubscription, UpgradeDialog } from "@/lib/subscription";
import { useAuth } from "@/lib/contexts/auth.context";
import { SystemRole } from "@/lib/auth/auth.types";
import { usePlayerStore } from "@/lib/player/player.store";
import { AnalyticsService } from "@/lib/analytics/analytics.service";

interface GlobalChatProps {
	defaultOpen?: boolean;
	onOpenChange?: (open: boolean) => void;
	podWorkspaceId?: string;
	workspaceTitle?: string;
	hideTrigger?: boolean;
	forceNewChat?: boolean;
}

/**
 * Citation badge component that shows source number with tooltip
 */
function CitationBadge({
	number,
	source,
}: {
	number: number;
	source: ChatSource;
}) {
	const text = source.text_full || source.text_snippet;

	return (
		<TooltipProvider>
			<Tooltip>
				<TooltipTrigger asChild>
					<button
						type="button"
						className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[9px] font-bold text-primary bg-gradient-to-br backdrop-blur-sm border border-orange-300/50 rounded-full shadow-sm hover:shadow-md hover:scale-110 hover:border-orange-400/40 dark:hover:border-orange-400/35 hover:from-orange-200/50 hover:via-orange-100/35 hover:to-orange-200/25 dark:hover:from-orange-500/25 dark:hover:via-orange-600/15 dark:hover:to-orange-500/10 transition-all duration-300 ease-out mx-0.5 align-super cursor-help relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-br before:from-white/20 before:to-transparent before:opacity-0 hover:before:opacity-100 before:transition-opacity"
					>
						{number}
					</button>
				</TooltipTrigger>
				<TooltipContent
					side="top"
					className="max-w-[calc(100vw-2rem)] md:max-w-md p-3"
				>
					<div className="space-y-2">
						<div className="flex items-center gap-2">
							<span className="flex-shrink-0 flex items-center justify-center w-5 h-5 rounded-full bg-orange-500 text-white text-[10px] font-bold">
								{number}
							</span>
							<p className="text-xs font-semibold">{source.chapter_title}</p>
						</div>
						<div className="max-h-[200px] md:max-h-[200px] overflow-y-auto pr-2">
							<p className="text-xs text-muted-foreground leading-relaxed">
								{text}
							</p>
						</div>
						{/* <div className="flex items-center gap-2">
							<div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
								<div
									className="h-full bg-orange-500 rounded-full"
									style={{
										width: `${Math.round(source.similarity_score * 100)}%`,
									}}
								/>
							</div>
							<span className="text-[10px] font-semibold text-orange-600 dark:text-orange-400">
								{Math.round(source.similarity_score * 100)}%
							</span>
						</div> */}
					</div>
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
}

/**
 * Renders message content with inline citation badges
 */
function renderMessageWithCitations(
	content: string,
	sources?: ChatSource[],
): React.ReactNode {
	if (!sources || sources.length === 0) {
		return <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>;
	}

	// Custom text renderer that replaces [Source N] with citation badges
	const components = {
		// biome-ignore lint/suspicious/noExplicitAny: ReactMarkdown types
		p: ({ node, children, ...props }: any) => {
			const processedChildren = React.Children.map(children, (child) => {
				if (typeof child === "string") {
					return processTextWithCitations(child, sources);
				}
				return child;
			});
			return <p {...props}>{processedChildren}</p>;
		},
		// biome-ignore lint/suspicious/noExplicitAny: ReactMarkdown types
		li: ({ node, children, ...props }: any) => {
			const processedChildren = React.Children.map(children, (child) => {
				if (typeof child === "string") {
					return processTextWithCitations(child, sources);
				}
				return child;
			});
			return <li {...props}>{processedChildren}</li>;
		},
	};

	return (
		<ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
			{content}
		</ReactMarkdown>
	);
}

/**
 * Process text and replace [Source N] patterns with citation badges
 * Handles multiple citation formats and languages:
 * - [Source 1]
 * - [Source 1, 2, 3]
 * - [Source 1, Source 2, Source 3]
 * - [Source 1, 2, and 3]
 * - [Source 1, Source 2, and Source 3]
 * - [Quelle 1] (German fallback)
 * - [Src 1] (abbreviated fallback)
 */
function processTextWithCitations(
	text: string,
	sources: ChatSource[],
): React.ReactNode {
	const parts: React.ReactNode[] = [];
	// Pattern matches citation formats in multiple languages:
	// - [Source ...] (English)
	// - [Quelle ...] (German)
	// - [Src ...] (abbreviated)
	// This provides defense-in-depth in case the LLM translates citations
	const sourcePattern = /\[(Source|Quelle|Src)[^\]]+\]/gi;
	let lastIndex = 0;

	// Reset regex lastIndex
	sourcePattern.lastIndex = 0;

	let match = sourcePattern.exec(text);
	while (match !== null) {
		const currentMatch = match;

		// Add text before citation
		if (currentMatch.index > lastIndex) {
			parts.push(text.slice(lastIndex, currentMatch.index));
		}

		// Extract all numbers from the matched text
		// This works for both "[Source 1, 2, 3]" and "[Source 1, Source 2, Source 3]"
		const matchedText = currentMatch[0];
		const numberPattern = /\d+/g;
		const sourceNumbers: number[] = [];

		let numMatch = numberPattern.exec(matchedText);
		while (numMatch !== null) {
			const num = Number.parseInt(numMatch[0], 10);
			if (!Number.isNaN(num)) {
				sourceNumbers.push(num);
			}
			numMatch = numberPattern.exec(matchedText);
		}

		// Add citation badges for each source
		sourceNumbers.forEach((sourceNum, idx) => {
			const source = sources[sourceNum - 1];
			if (source) {
				parts.push(
					<CitationBadge
						key={`cite-${currentMatch.index}-${sourceNum}-${idx}`}
						number={sourceNum}
						source={source}
					/>,
				);
			}
		});

		lastIndex = currentMatch.index + currentMatch[0].length;
		match = sourcePattern.exec(text);
	}

	// Add remaining text
	if (lastIndex < text.length) {
		parts.push(text.slice(lastIndex));
	}

	return parts.length > 0 ? <>{parts}</> : text;
}

/**
 * GlobalChat component
 * @param defaultOpen - Whether the chat is open by default
 * @param onOpenChange - Callback function to handle open state changes
 * @param podWorkspaceId - The ID of the podcast workspace to chat with
 * @param podcastIds - The IDs of the podcasts to chat with
 * @returns The GlobalChat component
 */
export function GlobalChat({
	defaultOpen = false,
	onOpenChange,
	podWorkspaceId,
	workspaceTitle,
	hideTrigger = false,
	forceNewChat = false,
}: GlobalChatProps) {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(defaultOpen);
	const [showHistory, setShowHistory] = useState(false);
	const [showWorkspaceSelector, setShowWorkspaceSelector] = useState(false);
	const [input, setInput] = useState("");
	const [activeWorkspaceId, setActiveWorkspaceId] = useState<
		string | undefined
	>(podWorkspaceId);
	const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
	const scrollRef = useRef<HTMLDivElement>(null);
	const loadingIndicatorRef = useRef<HTMLDivElement>(null);
	const lastAiMessageRef = useRef<HTMLDivElement>(null);
	const historyButtonClickedRef = useRef<number>(0);
	const previousMessageCountRef = useRef(0);

	// Auth context
	const { user } = useAuth();

	// Subscription hooks
	const { chatLimit, incrementUsage, usage, limits } = useSubscription();

	// Check if user is a platform manager
	const isPlatformManager = user?.systemRoles?.includes(
		SystemRole.PLATFORM_MANAGER,
	);

	// Use chat store
	const {
		messages,
		chatHistory,
		currentChatId,
		currentPodWorkspaceId,
		connectedWorkspaceIds,
		isInitializing,
		isSendingMessage,
		isLoadingHistory,
		initializeChat,
		sendMessage,
		setCurrentChat,
		setConnectedWorkspaces,
		reset,
		loadAllChatHistory,
	} = useChatStore();

	// Smart scroll behavior:
	// 1. When AI is thinking (isSendingMessage), scroll to show the loading indicator
	// 2. When AI answer arrives, scroll to the beginning of the new AI message
	useEffect(() => {
		if (isSendingMessage && loadingIndicatorRef.current) {
			// Scroll to show the "..." loading indicator
			loadingIndicatorRef.current.scrollIntoView({
				behavior: "smooth",
				block: "center",
			});
		}
	}, [isSendingMessage]);

	// Track when a new AI message arrives
	useEffect(() => {
		const currentMessageCount = messages.length;
		const previousMessageCount = previousMessageCountRef.current;

		// If message count increased and we're not sending anymore, a new AI message arrived
		if (
			currentMessageCount > previousMessageCount &&
			!isSendingMessage &&
			previousMessageCount > 0
		) {
			// Find the last assistant message (newest AI response)
			const lastAiMessage = messages
				.slice()
				.reverse()
				.find((msg) => msg.role === "assistant");

			if (lastAiMessage && lastAiMessageRef.current) {
				// Scroll to the beginning of the new AI answer
				lastAiMessageRef.current.scrollIntoView({
					behavior: "smooth",
					block: "start",
				});
			}
		}

		previousMessageCountRef.current = currentMessageCount;
	}, [messages, isSendingMessage]);

	// Sync open state with prop
	useEffect(() => {
		setIsOpen(defaultOpen);
	}, [defaultOpen]);

	// Sync active workspace with prop (only if there's a podcast in the player store)
	useEffect(() => {
		const playerPodWorkspaceId = usePlayerStore.getState().podWorkspaceId;
		const hasPodcastInPlayer = !!playerPodWorkspaceId;

		// Only set activeWorkspaceId if there's a podcast in the player store
		if (podWorkspaceId && hasPodcastInPlayer) {
			setActiveWorkspaceId(podWorkspaceId);
		} else if (!hasPodcastInPlayer) {
			// Clear activeWorkspaceId if there's no podcast in player
			setActiveWorkspaceId(undefined);
		}
	}, [podWorkspaceId]);

	// Initialize chat when opened and workspace is selected
	// Note: We intentionally only depend on isOpen and activeWorkspaceId to prevent
	// duplicate chat creation from re-renders. The other values are captured on first render.
	// biome-ignore lint/correctness/useExhaustiveDependencies: Intentionally limiting dependencies to prevent duplicate chat creation
	useEffect(() => {
		if (isOpen && activeWorkspaceId) {
			initializeChat(activeWorkspaceId, workspaceTitle, forceNewChat);
		}
	}, [isOpen, activeWorkspaceId]);

	// Note: connectedWorkspaceIds is managed in the store and synced from backend
	// When user selects additional workspaces, we update the store directly

	// Note: We now track messages, not chat creation
	// Message count is incremented in handleSend after successful message send

	// Reset when workspace changes
	useEffect(() => {
		return () => {
			if (!isOpen) {
				reset();
			}
		};
	}, [isOpen, reset]);

	// Scroll to bottom when chat is opened
	useEffect(() => {
		if (isOpen && !isInitializing && scrollRef.current) {
			// Use setTimeout to ensure DOM is updated
			setTimeout(() => {
				scrollRef.current?.scrollIntoView({
					behavior: "smooth",
					block: "end",
				});
			}, 100);
		}
	}, [isOpen, isInitializing]);

	const handleSend = async () => {
		if (
			!input.trim() ||
			isSendingMessage ||
			!currentChatId ||
			!currentPodWorkspaceId
		)
			return;

		// Check if user has reached message limit before sending
		// Now we check EVERY message, not just the first one
		if (!chatLimit.allowed) {
			setShowUpgradeDialog(true);
			return;
		}

		const message = input.trim();
		setInput("");

		try {
			// Send all selected workspace IDs as a single array
			// If no workspaces are selected, use the current workspace as default
			const workspaceIds =
				connectedWorkspaceIds.length > 0
					? connectedWorkspaceIds
					: currentPodWorkspaceId
						? [currentPodWorkspaceId]
						: undefined;
			await sendMessage(message, workspaceIds);

			// Track chat message conversion
			AnalyticsService.track("chat_message_sent", {
				chat_id: currentChatId ?? undefined,
				workspace_id: currentPodWorkspaceId ?? undefined,
				connected_workspace_count: workspaceIds?.length,
			});

			// Increment message count after successful send
			// This gives optimistic feedback - backend is the source of truth
			incrementUsage("chats");
		} catch (error) {
			console.error("Failed to send message:", error);
			toast.error(t("chat.sendFailed"));
		}
	};

	const handleSelectChat = (chatId: string) => {
		setCurrentChat(chatId);
		setShowHistory(false);
	};

	const handleToggleHistory = (e?: React.MouseEvent | React.TouchEvent) => {
		e?.stopPropagation();
		e?.preventDefault();

		// Prevent double-clicks with debounce mechanism
		const now = Date.now();
		if (now - historyButtonClickedRef.current < 500) {
			// Ignore clicks within 500ms of the last click
			return;
		}
		historyButtonClickedRef.current = now;

		loadAllChatHistory();
		setShowHistory(!showHistory);
	};

	const handleNewChat = (e?: React.MouseEvent | React.TouchEvent) => {
		e?.stopPropagation();
		e?.preventDefault();

		// Check if there's a podcast currently playing in the player store
		const playerPodWorkspaceId = usePlayerStore.getState().podWorkspaceId;
		const hasPodcastInPlayer = !!playerPodWorkspaceId;

		if (hasPodcastInPlayer && playerPodWorkspaceId) {
			// If listening to something, use that workspace and create a new chat
			reset();
			setActiveWorkspaceId(playerPodWorkspaceId);
			initializeChat(playerPodWorkspaceId, workspaceTitle, true)
				.then(() => {
					toast.success(t("chat.newChatStarted"));
				})
				.catch((error) => {
					console.error("Failed to create new chat:", error);
					toast.error(t("chat.failedToCreateChat"));
				});
		} else {
			// If not listening to anything, reset and let user select a workspace
			reset();
			setActiveWorkspaceId(undefined);
			toast.success(t("chat.newChatStarted"));
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSend();
		}
	};

	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
		onOpenChange?.(open);
	};

	// Mobile gesture support for swipe-down to close
	const { dragState, bind } = useMobileChatGestures({
		onClose: () => handleOpenChange(false),
	});

	return (
		<>
			{/* Upgrade Dialog */}
			<UpgradeDialog
				open={showUpgradeDialog}
				onOpenChange={setShowUpgradeDialog}
				limitType="chat"
				limitCheck={chatLimit}
			/>

			{!hideTrigger && (
				<Button
					onClick={() => handleOpenChange(true)}
					variant="ghost"
					size="icon"
					className="h-8 w-8 text-orange-400 hover:text-orange-300"
					title={t("chat.openChat")}
				>
					<MessageCircle className="h-5 w-5" />
				</Button>
			)}

			{isOpen && (
				<div className="fixed inset-0 z-[75] md:z-50 flex items-end md:justify-end">
					<button
						type="button"
						className="absolute inset-0 bg-black/50 md:bg-black/20 backdrop-blur-md md:backdrop-blur-none cursor-default"
						onClick={() => handleOpenChange(false)}
						aria-label={t("chat.closeChat")}
					/>
					<Card
						className="relative w-full gap-2 md:w-[500px] md:max-w-[500px] h-full md:h-[600px] md:max-h-[600px] flex flex-col shadow-2xl md:m-4 md:mb-28 py-2 overflow-hidden md:rounded-lg rounded-none"
						onClick={(e) => e.stopPropagation()}
						style={{
							transform: dragState.isDragging
								? `translateY(${dragState.y}px)`
								: undefined,
							transition: dragState.isDragging
								? "none"
								: "transform 0.3s ease-out",
						}}
					>
						{/* Mobile drag handle - only visible on mobile */}
						<div
							className="flex md:hidden items-center justify-center py-2 touch-none cursor-grab active:cursor-grabbing"
							{...bind()}
						>
							<div className="w-12 h-1.5 bg-muted-foreground/30 rounded-full" />
						</div>

						<div className="flex items-center justify-between border-b border-border p-4 md:pt-4 pt-2 shrink-0">
							<div className="flex items-center gap-2">
								<div>
									<h3 className="font-semibold text-foreground">
										{t("chat.title")}
									</h3>
									<p className="text-xs text-muted-foreground">
										{t("chat.subtitle")}
									</p>
									{/* Platform Manager: Show message limits */}
									{isPlatformManager && limits && (
										<div className="mt-1 flex items-center gap-1.5 text-xs">
											<span className="text-orange-500 font-medium">
												Messages:
											</span>
											<span className="text-muted-foreground">
												{usage.chats}/
												{typeof chatLimit.maxAllowed === "number"
													? chatLimit.maxAllowed
													: "∞"}{" "}
												sent
											</span>
											{chatLimit.remaining !== "unlimited" && (
												<span
													className={`font-medium ${
														chatLimit.isAtLimit
															? "text-red-500"
															: chatLimit.isNearLimit
																? "text-yellow-500"
																: "text-green-500"
													}`}
												>
													({chatLimit.remaining} left)
												</span>
											)}
										</div>
									)}
								</div>
							</div>
							<div className="flex items-center gap-1">
								{/* New Chat Button - Mobile */}
								<Button
									variant="outline"
									size="icon"
									onClick={(e) => handleNewChat(e)}
									className="h-8 w-8 rounded-full touch-manipulation md:hidden text-orange-500 hover:text-orange-400 hover:bg-orange-500/10"
									title={t("chat.newChat")}
								>
									<Plus className="h-4 w-4" />
								</Button>
								{/* New Chat Button - Desktop with Tooltip */}
								<TooltipProvider>
									<Tooltip>
										<TooltipTrigger asChild>
											<Button
												variant="outline"
												size="icon"
												onClick={(e) => handleNewChat(e)}
												className="h-8 w-8 border-primary rounded-full touch-manipulation hidden md:flex text-orange-500 hover:text-orange-400 hover:bg-orange-500/10"
											>
												<Plus className="h-4 w-4" />
											</Button>
										</TooltipTrigger>
										<TooltipContent>
											<div className="text-center">
												<p className="font-semibold">{t("chat.newChat")}</p>
												<p className="text-xs text-muted-foreground mt-1">
													{t("chat.newChatDescription")}
												</p>
											</div>
										</TooltipContent>
									</Tooltip>
								</TooltipProvider>
								{/* Divider */}
								<div className="h-6 w-px bg-border mx-1" />
								{/* History Button - Mobile */}
								<Button
									variant="ghost"
									size="icon"
									onClick={(e) => handleToggleHistory(e)}
									className="h-8 w-8 rounded-full touch-manipulation md:hidden"
									title={t("chat.history.title")}
								>
									<History className="h-4 w-4" />
								</Button>
								{/* History Button - Desktop with Tooltip */}
								<TooltipProvider>
									<Tooltip>
										<TooltipTrigger asChild>
											<Button
												variant="ghost"
												size="icon"
												onClick={(e) => handleToggleHistory(e)}
												className="h-8 w-8 rounded-full touch-manipulation hidden md:flex"
											>
												<History className="h-4 w-4" />
											</Button>
										</TooltipTrigger>
										<TooltipContent>
											<p>{t("chat.history.title")}</p>
										</TooltipContent>
									</Tooltip>
								</TooltipProvider>
								{/* Close Button */}
								<Button
									variant="ghost"
									size="icon"
									onClick={() => handleOpenChange(false)}
									className="h-8 w-8 rounded-full"
								>
									<X className="h-4 w-4" />
								</Button>
							</div>
						</div>

						{/* Workspace Selector */}
						<WorkspaceSelector
							currentWorkspaceId={currentPodWorkspaceId ?? undefined}
							selectedWorkspaceIds={connectedWorkspaceIds}
							open={showWorkspaceSelector}
							onOpenChange={setShowWorkspaceSelector}
							onSelectionChange={() => {
								// Don't close selector - just update the selection locally
								// The selector will close when user clicks save/close button
								// We'll apply changes when the selector closes
							}}
							onSave={async (ids) => {
								// Apply changes when user clicks save
								if (!currentPodWorkspaceId && ids.length > 0) {
									const firstWorkspace = ids[0];
									setActiveWorkspaceId(firstWorkspace);
									// Initialize chat will set currentPodWorkspaceId in the store
									// We pass ids to initializeChat so it sets them immediately without flashing
									await initializeChat(firstWorkspace, undefined, false, ids);
									setShowWorkspaceSelector(false);
								} else {
									// Update connected workspaces in the store (and persist to backend)
									await setConnectedWorkspaces(ids);
									setShowWorkspaceSelector(false);
								}
							}}
							allowPrimarySelection={!currentPodWorkspaceId}
						/>

						{/* Chat History Sidebar */}
						{showHistory && (
							<ChatHistory
								chatHistory={chatHistory}
								currentChatId={currentChatId}
								currentPodWorkspaceId={currentPodWorkspaceId}
								isLoadingHistory={isLoadingHistory}
								onSelectChat={handleSelectChat}
								onClose={() => setShowHistory(false)}
							/>
						)}

						<ScrollArea
							className="flex-1 px-2 md:px-2 py-0 min-h-0"
							data-chat-scroll
						>
							<div className="space-y-2 px-2 py-2">
								{!currentPodWorkspaceId ? (
									<div className="flex items-center justify-center p-8 h-full">
										<div className="text-center space-y-3 max-w-sm">
											<div className="flex justify-center">
												<MessageCircle className="h-12 w-12 text-muted-foreground/50" />
											</div>
											<h4 className="font-semibold text-foreground">
												{t("chat.selectWorkspace")}
											</h4>
											<p className="text-sm text-muted-foreground">
												{t("chat.selectWorkspaceDescription")}
											</p>
										</div>
									</div>
								) : isInitializing ? (
									<div className="flex items-center justify-center p-8">
										<div className="flex items-center gap-2 text-muted-foreground">
											<div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
											<span className="text-sm">{t("chat.initializing")}</span>
										</div>
									</div>
								) : (
									<>
										{messages.map((message, index) => {
											// Check if this is the last AI message
											const isLastAiMessage =
												message.role === "assistant" &&
												index === messages.length - 1;

											return (
												<div
													key={message.id}
													ref={isLastAiMessage ? lastAiMessageRef : null}
													className={`flex gap-3 ${message.role === "user" ? "justify-end" : "justify-start"}`}
												>
													<div
														className={`max-w-[85%] md:max-w-[80%] rounded-2xl md:rounded-lg px-4 py-3 md:py-2 ${
															message.role === "user"
																? "bg-primary text-primary-foreground"
																: "bg-card text-foreground"
														}`}
													>
														<div className="text-sm leading-relaxed prose prose-sm max-w-none dark:prose-invert">
															{renderMessageWithCitations(
																message.content,
																message.sources,
															)}
														</div>
													</div>
												</div>
											);
										})}

										{isSendingMessage && (
											<div ref={loadingIndicatorRef} className="flex gap-3">
												<div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary">
													<Bot className="h-4 w-4 text-primary-foreground" />
												</div>
												<div className="flex items-center gap-1 rounded-lg bg-muted px-4 py-2">
													<div className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground [animation-delay:-0.3s]" />
													<div className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground [animation-delay:-0.15s]" />
													<div className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground" />
												</div>
											</div>
										)}
									</>
								)}
								<div ref={scrollRef} />
							</div>
						</ScrollArea>

						<div
							className="border-t border-border p-4 shrink-0 bg-black/95 md:bg-transparent"
							style={{
								paddingBottom: "max(1rem, env(safe-area-inset-bottom))",
							}}
						>
							<div className="flex gap-2 items-end">
								<Textarea
									placeholder={
										!currentPodWorkspaceId
											? t("chat.selectWorkspaceFirst")
											: t("chat.placeholder")
									}
									value={input}
									onChange={(e) => {
										// Prevent input when no workspace is selected
										if (!currentPodWorkspaceId) {
											setShowWorkspaceSelector(true);
											return;
										}
										setInput(e.target.value);
									}}
									onKeyDown={handleKeyDown}
									onFocus={() => {
										// Open workspace selector if no workspace is selected
										if (!currentPodWorkspaceId) {
											setShowWorkspaceSelector(true);
										}
									}}
									readOnly={!currentPodWorkspaceId}
									className="min-h-[56px] md:min-h-[60px] max-h-[100px] md:max-h-[120px] resize-none bg-gray-800/50 md:bg-background text-white md:text-foreground placeholder:text-gray-400 md:placeholder:text-muted-foreground border-gray-700 md:border-input text-base md:text-sm touch-manipulation cursor-text"
									rows={1}
								/>
								<Button
									size="icon"
									onClick={handleSend}
									disabled={
										!currentPodWorkspaceId ||
										!input.trim() ||
										isSendingMessage ||
										isInitializing
									}
									className="h-[56px] w-[56px] md:h-[60px] md:w-[60px] shrink-0 rounded-full touch-target"
								>
									<Send className="h-5 w-5" />
								</Button>
							</div>
						</div>
					</Card>
				</div>
			)}
		</>
	);
}
